




















































import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
} from "@vue/composition-api";
import { clone, debounce } from "lodash-es";
import { TranslateResult } from "vue-i18n";
import { TagInfo } from "@scrile/api-provider/dist/api/TagsProvider";
import BaseButton from "@/components/BaseButton";
import BaseInput from "@/components/BaseInput";
import BasePage from "@/components/BasePage";
import BaseSpinner from "@/components/BaseSpinner";
import CustomScroll from "@/components/CustomScroll";
import TagItem from "@/components/TagItem";

export default defineComponent({
  name: "SelectTagsView",
  components: { TagItem, BasePage, BaseButton, BaseSpinner, CustomScroll, BaseInput },
  props: {
    selectedTagIds: { type: Array as PropType<string[]>, default: () => [] },
    tags: { type: Array as PropType<TagInfo[]>, required: true },
    showSaveButton: { type: Boolean, default: false },
    saveButtonText: { type: String as PropType<TranslateResult | string>, required: true },
    minSelectedTags: { type: Number, required: true },
    maxSelectedTags: { type: Number, required: true },
    maxRows: { type: Number, default: 0 },
    scrollable: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const saving = ref(false);
    const showAllTags = ref(false);
    const searchTagValue = ref("");
    const tagsContainer = ref<HTMLElement>();
    const inputRef = ref<Vue>();
    const visibleTags = ref<TagInfo[]>([]);
    const selectedTags = ref<TagInfo[]>(props.tags.filter(tag => props.selectedTagIds.includes(tag.id)));

    const saveButtonDisabled = computed(() => selectedTags.value.length < props.minSelectedTags);
    const filteredTags = computed<TagInfo[]>(() => {
      return clone(props.tags)
        .sort((a, b) => {
          const normalizeA = a.title.toLowerCase();
          const normalizeB = b.title.toLowerCase();
          return normalizeA > normalizeB ? 1 : normalizeA < normalizeB ? -1 : 0;
        })
        .filter(tag => tag.title.toLowerCase().includes(searchTagValue.value.toLowerCase()));
    });
    const hiddenTagsCount = computed(() => filteredTags.value.length - visibleTags.value.length);

    const calculateVisibleTags = async () => {
      if (showAllTags.value || !props.maxRows) {
        visibleTags.value = filteredTags.value;
        return;
      }
      if (!tagsContainer.value) return;

      visibleTags.value = [];
      const tagHeight = 40;
      const gap = 8;
      const maxHeight = props.maxRows * tagHeight + gap * (props.maxRows - 1);

      for (const tag of filteredTags.value) {
        if (tagsContainer.value.scrollHeight > maxHeight && visibleTags.value.length) break;
        visibleTags.value.push(tag);
        await nextTick();

        if (tagsContainer.value.scrollHeight > maxHeight) {
          visibleTags.value.pop();
        }
      }
    };

    const onResize = debounce(calculateVisibleTags, 10, { leading: true, trailing: true });

    const onShowToggle = () => {
      showAllTags.value = !showAllTags.value;
      if (showAllTags.value) {
        visibleTags.value = filteredTags.value;
      } else {
        (inputRef.value?.$refs.inputElement as HTMLInputElement)?.focus();
        (inputRef.value?.$refs.inputElement as HTMLInputElement)?.blur();
        calculateVisibleTags();
      }
    };

    const selectTag = (tag: TagInfo, value: boolean) => {
      if (selectedTags.value.length >= props.maxSelectedTags && value) return;
      if (value) {
        selectedTags.value.push(tag);
      } else {
        selectedTags.value = selectedTags.value.filter(t => t.id !== tag.id);
      }
      emit("input", selectedTags.value);
    };

    const isTagSelected = (tag: TagInfo) => !!selectedTags.value.find(t => t.id === tag.id);

    const save = () => {
      saving.value = true;
      emit("save", selectedTags.value, () => (saving.value = false));
    };

    const clearTags = () => (selectedTags.value = []);

    emit("clear", clearTags);
    emit("input", selectedTags.value);

    onMounted(() => {
      watch(filteredTags, calculateVisibleTags, { immediate: true });

      if (props.maxRows) {
        window.addEventListener("resize", onResize, { passive: true });
      }
    });

    onUnmounted(() => {
      window.removeEventListener("resize", onResize);
    });

    return {
      saving,
      showAllTags,
      searchTagValue,
      tagsContainer,
      inputRef,
      selectedTags,
      visibleTags,
      saveButtonDisabled,
      hiddenTagsCount,
      onShowToggle,
      isTagSelected,
      selectTag,
      save,
    };
  },
});
